<template>
  <div
    id="login"
    class="authentication-container"
    :class="{'authentication-container-mobile': isMobileSize}"

  >

    <!-- LEFT SIDE -->

    <left-side v-if="!isMobileSize"/>

    <!-- RIGHT SIDE -->

    <div class="right-side">

      <!-- TITLE -->

      <div class="title">
        <h3 class="title-header">Login</h3>
      </div>

      <div class="content dialog-message text-primary-color mobile" v-if="organizationId">
        <p>Your email is already registered in Omadeus.</p>
        <p>Please login to join the Account you were invited to.</p>
      </div>

      <!-- INPUTS -->

      <form class="content form" :class="{'mobile': isMobileSize}">
        <div class="inputs">
          <div class="input-container">
            <label
              for="email"
              class="label"
            >Email or User ID</label>
            <input
              type="text"
              id="email"
              class="primary-input"
              v-model.trim="$v.credentials.email.$model"
              :disabled="googleToken || isDialogOpened"
              :class="{error: $v.credentials.email.$error}"
            >
            <validation-message
              :validation="$v.credentials.email"
              :metadata="memberMetadata.fields.email"
            />
          </div>

          <div class="input-container">
            <label
              for="password"
              class="label"
            >
              {{ memberMetadata.fields.password.label }}
            </label>

            <CoreInput
              id="password"
              type="password"
              class="primary-input password-input"
              required
              placeholder=""
              v-model="credentials.password"
              :disabled="googleToken || isDialogOpened"
              :error="$v.credentials.password.$error"
            />

            <password-validation-message
              :validation="$v.credentials.password"
              :metadata="memberMetadata.fields.password"
            />
          </div>

          <p
            class="link"
            @click="goToForgetPassword()"
          >
            Forgot your password?
          </p>
        </div>

        <!-- ACTIONS -->

        <div class="actions">
          <CoreButton
            @click.prevent="login"
            :disabled="$v.credentials.$invalid"
            :loading="isLoading"
          >
            Login
          </CoreButton>

          <CoreButton @click.prevent="loginWithGoogle" class="login-google">
            Login with Google
            <img
              src="@/assets/google-icon.png"
              alt="alt"
              width="18px"
            >
          </CoreButton>

          <CoreButton @click.prevent="createBasicOrganization" class="create-organization">
            Create Account
          </CoreButton>
        </div>
      </form>

      <!-- REGISTER PROMPT -->
      <div class="go-to-signup p-left-50" v-if="!organizationId">
        <p>
          <router-link
            :to="{path: '/signup'}"
            class="link"
          >
            Sign up
          </router-link> for the app
        </p>
      </div>

      <!-- GO TO SIGNUP -->

      <!-- <div class="go-to-signup p-left-50">
        <p>Don't have an account yet? <router-link
            :to="{path: '/register', query: getQueries()}"
            class="link"
          >Sign up</router-link>
        </p>
      </div> -->
    </div>

    <OpenAppDialog v-model="isDialogOpened" v-if="!organizationId" />
  </div>
</template>

<script>
import server from './../server'
import { mapMutations } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { mixin as clickout } from 'vue-clickout'
import { DEFAULT_REDIRECT_URL, DEFAULT_SCOPES, DEFAULT_APPLICATION_ID, GOOGLE_CLIENT_ID, GOOGLE_OAUTH_URL } from '../settings'
import { PandaService } from '../utils/request'
import { WaitStates } from '../utils/vuewait'
import { MazDialog, MazBtn } from 'maz-ui'

const jwtDecode = require('jwt-decode')

const CoreButton = () => import(
  /* webpackChunkName: "CoreButton" */ './../components/Core/CoreButton.vue'
)
const CoreInput = () => import(
  /* webpackChunkName: "CoreInput" */ './../components/Core/CoreInput.vue'
)
const LeftSide = () => import(
  /* webpackChunkName: "LeftSide" */ './../components/LeftSide'
)
const ValidationMessage = () => import(
  /* webpackChunkName: "ValidationMessage" */ './../components/ValidationMessage'
)
const PasswordValidationMessage = () => import(
  /* webpackChunkName: "PasswordValidationMessage" */ './../components/PasswordValidationMessage.vue'
)
const OpenAppDialog = () => import(
  /* webpackChunkName: "OpenAppDialog" */ './../components/OpenAppDialog.vue'
)

export default {
  mixins: [clickout],
  name: 'Login',
  data () {
    return {
      credentials: {
        email: null,
        password: ''
      },
      memberMetadata: server.metadata,
      // FIXME: REMOVE ALTERNATIVE VALUES AFTER MERGING CAS AND MAESTRO
      applicationId: this.$route.query.applicationId || DEFAULT_APPLICATION_ID,
      state: this.$route.query.state,
      redirectUri: this.$route.query.redirectUri || DEFAULT_REDIRECT_URL,
      scopes: this.$route.query.scopes || DEFAULT_SCOPES.join(','),
      token: this.$route.query.t_,
      organizationId: this.$route.query.organizationId,
      googleToken: this.$route.query.code,
      googleState: this.$route.query.state,
      isDialogOpened: false
    }
  },
  validations () {
    return {
      credentials: {
        email: {
          required
        },
        password: {
          required
        }
      }
    }
  },
  computed: {
    query () {
      let queries = {}
      if (this.applicationId) {
        queries.applicationId = this.applicationId
      }
      if (this.state) {
        queries.state = this.state
      }
      if (this.redirectUri) {
        queries.redirectUri = this.redirectUri
      }
      if (this.scopes) {
        queries.scopes = this.scopes
      }
      if (this.token) {
        let member = atob(this.token.split('.')[1])
        const organizationId = JSON.parse(member).organizationId
        queries.organizationId = String(organizationId)
      }
      if (this.$route.query.organizationId) {
        queries.organizationId = this.$route.query.organizationId
      }
      return queries
    },
    isLoading () {
      return this.$wait.is(WaitStates.LOGIN_LOADING)
    },
    loginFormData () {
      if (this.googleToken) {
        return {
          oauthCode: this.googleToken,
          redirectUrl: window.location.href.split('?')[0]
        }
      }
      return {
        email: this.credentials.email,
        password: this.credentials.password
      }
    },
    isMobileSize () {
      return window.innerWidth <= 768
    }
  },
  methods: {
    goToForgetPassword () {
      this.$router.push({ path: '/forgot_password', query: { organizationId: this.organizationId } })
    },

    loginWithGoogle () {
      window.location.href = `${GOOGLE_OAUTH_URL}?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${window.location.protocol}//${window.location.hostname}%2Flogin&scope=email+profile&state=${this.stringifyLoginData()}`
    },

    createBasicOrganization () {
      this.$router.push({ path: '/create_basic_account' })
    },

    async login () {
      this.$wait.start(WaitStates.LOGIN_LOADING)
      const { status, data } = await PandaService({
        url: 'tokens',
        method: 'CREATE',
        data: JSON.stringify(this.loginFormData)
      })
      this.$wait.end(WaitStates.LOGIN_LOADING)

      if (status === 200) {
        server.token = data.token
        const memberData = jwtDecode(data.token)
        this.state = memberData.email
        this.confirmIdentity()
        if (this.applicationId) {
          this.$router.push({ path: '/permissions', query: this.query })
        } else if (this.$route.query.origin) {
          this.$router.push(this.$route.query.origin)
        } else {
          this.$router.push('/')
        }
      }
    },
    stringifyLoginData () {
      const loginData = {
        token: this.token,
        applicationId: this.applicationId,
        scopes: this.scopes,
        organizationId: this.organizationId,
        redirectUri: this.redirectUri
      }

      return JSON.stringify(loginData)
    },
    // getQueries () {
    //   let queries = {}
    //   for (let query in this.$route.query) {
    //     queries[query] = this.$route.query[query]
    //   }
    //   if (Object.keys(queries).length) {
    //     return queries
    //   } else {
    //     return null
    //   }
    // },
    ...mapMutations([
      'confirmIdentity'
    ])
  },
  async beforeMount () {
    if (this.token) {
      let member = atob(this.token.split('.')[1])
      this.organizationId = JSON.parse(member).organizationId
    }
    if (this.googleToken) {
      const query = JSON.parse(this.googleState)

      this.applicationId = query.applicationId
      this.organizationId = query.organizationId
      this.scopes = query.scopes
      this.token = query.token
      this.redirectUri = query.redirectUri

      await this.login()
    }
  },
  components: {
    LeftSide,
    ValidationMessage,
    CoreButton,
    CoreInput,
    PasswordValidationMessage,
    OpenAppDialog,
    MazDialog,
    MazBtn
  }
}
</script>
